import {
  ArrowLeftOnRectangleIcon,
  Bars3BottomLeftIcon,
  CommandLineIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { cn } from '../../utils/common-utils.ts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../dropdown-menu.tsx';

type AttemptMoreMenuProps = {
  questionCountLabel?: string;
  onOpenQuestions?: () => void;
  onOpenReport?: () => void;
  onExit: () => void;
};

const triggerClassName =
  'mx-0.5 flex h-[4rem] w-[2.6rem] flex-col items-center justify-center rounded-md border border-transparent bg-transparent px-1.5 text-[#222] transition-colors hover:bg-[rgba(254,219,0,0.18)] focus:border-none focus:outline-none';
const activeTriggerClassName = 'underline decoration-current underline-offset-4';
const toolButtonIconRowClassName = 'flex h-7 items-center justify-center';
const toolButtonLabelClassName =
  'mt-1.5 whitespace-nowrap text-xs font-semibold leading-none';

const menuItemClassName =
  'cursor-pointer rounded-md px-4 py-3 text-[1.05rem] font-medium text-[#222] outline-none data-[highlighted]:bg-[#f3f4f6]';

const menuIconClassName = 'h-6 w-6 flex-shrink-0 text-[#222]';

const PlaceholderMenuItem = ({
  icon,
  label,
}: {
  icon: JSX.Element;
  label: string;
}) => (
  <DropdownMenuItem
    className={menuItemClassName}
    onSelect={() => undefined}
  >
    <span className="mr-4">{icon}</span>
    <span>{label}</span>
  </DropdownMenuItem>
);

const AttemptMoreMenu = ({
  questionCountLabel,
  onOpenQuestions,
  onOpenReport,
  onExit,
}: AttemptMoreMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeThenRun = (callback: () => void) => {
    setIsOpen(false);
    window.requestAnimationFrame(() => {
      callback();
    });
  };

  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(triggerClassName, isOpen && activeTriggerClassName)}
          type="button"
        >
          <div className={toolButtonIconRowClassName}>
            <EllipsisVerticalIcon className="h-6 w-6" />
          </div>
          <div className={toolButtonLabelClassName}>More</div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[18rem] rounded-md border border-[#d1d5db] bg-white p-2 shadow-[0_18px_40px_rgba(0,0,0,0.2)]"
        sideOffset={8}
      >
        {questionCountLabel && onOpenQuestions && (
          <>
            <DropdownMenuItem
              className={menuItemClassName}
              onSelect={onOpenQuestions}
            >
              <span className="mr-4 block h-6 w-6" />
              <span className="font-semibold">{questionCountLabel}</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator className="mx-2 my-1 bg-[#e5e7eb]" />
          </>
        )}

        <PlaceholderMenuItem
          icon={<QuestionMarkCircleIcon className={menuIconClassName} />}
          label="Help"
        />
        <PlaceholderMenuItem
          icon={<CommandLineIcon className={menuIconClassName} />}
          label="Shortcuts"
        />
        <PlaceholderMenuItem
          icon={<HandRaisedIcon className={menuIconClassName} />}
          label="Assistive Technology"
        />
        <PlaceholderMenuItem
          icon={<Bars3BottomLeftIcon className={menuIconClassName} />}
          label="Line Reader"
        />

        {onOpenReport && (
          <DropdownMenuItem
            className={menuItemClassName}
            onSelect={onOpenReport}
          >
            <span className="mr-4">
              <ExclamationTriangleIcon className={menuIconClassName} />
            </span>
            <span>Report Question</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          className={menuItemClassName}
          onSelect={(event) => {
            event.preventDefault();
            closeThenRun(onExit);
          }}
        >
          <span className="mr-4">
            <ArrowLeftOnRectangleIcon className={menuIconClassName} />
          </span>
          <span>Exit the Exam</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AttemptMoreMenu;
